import { useEffect, useRef, useState } from "react";
import { BellRing, Loader2, CheckCircle2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const CONSENT_TEXT =
  "Email me about upcoming MacWay estate sales, including sale updates and address releases. I can unsubscribe at any time.";

export const NOTIFY_OPEN_EVENT = "macway:open-notify";

export const NotifySignup = ({ sourcePage = "upcoming-estate-sales" }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const sectionRef = useRef(null);

  // Any "Get Notified" button on the page can open this single shared form
  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    };
    window.addEventListener(NOTIFY_OPEN_EVENT, handler);
    return () => window.removeEventListener(NOTIFY_OPEN_EVENT, handler);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/subscribers", {
        email,
        first_name: firstName,
        source_page: sourcePage,
        consent,
        consent_text: CONSENT_TEXT,
        website: honeypot,
      });
      setSuccess(data.message);
      if (!data.already && typeof window.gtag === "function") {
        // GA4: track the signup — never send name or email
        window.gtag("event", "estate_sale_signup", { method: "notify_form" });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={sectionRef} className="bg-forest text-ivory py-16 md:py-20 scroll-mt-24" data-testid="notify-signup">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <BellRing className="w-10 h-10 text-brass mx-auto" />
        <h2 className="mt-5 text-4xl sm:text-5xl font-medium tracking-tight" data-testid="notify-heading">
          Don&rsquo;t Miss the Sale
        </h2>
        <p className="mt-4 text-base sm:text-lg text-ivory/80 leading-relaxed max-w-xl mx-auto" data-testid="notify-description">
          Sign up for email updates about upcoming MacWay estate sales, including merchandise previews, sale
          reminders, and the address when it&rsquo;s released.
        </p>

        {success ? (
          <div className="mt-8 bg-ivory/10 border border-brass/50 px-6 py-6 inline-flex items-start gap-3 text-left max-w-xl" data-testid="notify-success" role="status">
            <CheckCircle2 className="w-6 h-6 text-brass shrink-0 mt-0.5" />
            <p className="text-base text-ivory leading-relaxed">{success}</p>
          </div>
        ) : !open ? (
          <button onClick={() => setOpen(true)} className="btn-brass mt-8" data-testid="notify-open-button" aria-expanded="false" aria-controls="notify-form-panel">
            Get Notified
          </button>
        ) : (
          <form
            id="notify-form-panel"
            onSubmit={handleSubmit}
            className="mt-8 bg-ivory text-ink p-7 md:p-8 text-left max-w-xl mx-auto"
            data-testid="notify-form"
          >
            <div style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }} aria-hidden="true">
              <label htmlFor="nf-website">Website</label>
              <input id="nf-website" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="nf-email" className="form-label">
                  Email address <span className="text-brass">*</span>
                </label>
                <input
                  id="nf-email"
                  type="email"
                  className="form-input"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="notify-email-input"
                />
              </div>
              <div>
                <label htmlFor="nf-name" className="form-label">
                  First name <span className="font-normal text-ink/50">(optional)</span>
                </label>
                <input
                  id="nf-name"
                  type="text"
                  className="form-input"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  data-testid="notify-name-input"
                />
              </div>
            </div>

            <div className="mt-5 flex items-start gap-3">
              <input
                id="nf-consent"
                type="checkbox"
                className="mt-1 w-4 h-4 accent-[#1A3626]"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                required
                data-testid="notify-consent-checkbox"
              />
              <label htmlFor="nf-consent" className="text-sm text-ink/70 leading-relaxed">
                {CONSENT_TEXT}
              </label>
            </div>

            {error && (
              <p className="mt-4 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 px-4 py-3" role="alert" data-testid="notify-error">
                {error}
              </p>
            )}

            <div className="mt-6 flex items-center gap-4">
              <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="notify-submit">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Signing up…
                  </>
                ) : (
                  "Sign Me Up"
                )}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink/50 hover:text-ink transition-colors"
                data-testid="notify-cancel"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
